/** Custom date-range picker with presets + calendar.
 *  Active mode (`granularity`) is one of the standard granularities
 *  (daily/weekly/445/quarterly/ytd) OR "custom" with explicit from/to.
 *  Presets supported: yesterday, this_week, this_month + the full set of standard granularities.
 */
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarBlank, Check } from "@phosphor-icons/react";

const PRESETS = [
  { v: "daily", l: "Today" },
  { v: "yesterday", l: "Yesterday" },
  { v: "last_week", l: "Last Week" },
  { v: "weekly", l: "This Week" },
  { v: "monthly", l: "This Month" },
  { v: "last_month", l: "Last Month" },
];

function ymd(d) {
  // Use local-date components — `toISOString()` converts to UTC and shifts the
  // date back a day for users in any non-UTC timezone (e.g. BST).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtUK(s) {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y.slice(2)}`;
}

export default function DateRangePicker({ value, onChange }) {
  // value = { granularity, date_from, date_to }
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({
    from: value?.date_from ? new Date(value.date_from) : undefined,
    to: value?.date_to ? new Date(value.date_to) : undefined,
  });

  const applyPreset = (g) => {
    onChange({ granularity: g, date_from: null, date_to: null });
    setOpen(false);
  };
  const applyRange = () => {
    if (!range?.from) return;
    const to = range.to || range.from;
    onChange({ granularity: "custom", date_from: ymd(range.from), date_to: ymd(to) });
    setOpen(false);
  };

  const label = (() => {
    if (!value) return "Pick a range";
    if (value.granularity === "custom") {
      const f = fmtUK(value.date_from);
      const t = fmtUK(value.date_to);
      return f === t ? f : `${f} → ${t}`;
    }
    const preset = PRESETS.find((p) => p.v === value.granularity);
    return preset?.l || "Custom";
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid="date-range-trigger"
          className="h-10 rounded-md border-[#E2E8F0] bg-white text-xs font-medium tracking-tight pl-3 pr-4"
        >
          <CalendarBlank size={14} className="mr-2 text-[#475569]" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 rounded-md border-[#E2E8F0]"
        align="start"
        data-testid="date-range-popover"
      >
        <div className="flex">
          {/* Presets sidebar */}
          <div className="flex flex-col w-44 border-r border-[#EEEEEE] p-2 gap-0.5">
            <p className="uppercase text-[11px] tracking-[0.2em] text-[#94A3B8] font-medium px-2 py-2">Quick range</p>
            {PRESETS.map((p) => {
              const active = value?.granularity === p.v;
              return (
                <button
                  key={p.v}
                  data-testid={`date-preset-${p.v}`}
                  onClick={() => applyPreset(p.v)}
                  className={`flex items-center justify-between px-2.5 py-2 rounded text-xs text-left transition-colors ${
                    active ? "bg-[#0F172A] text-white" : "text-[#0F172A] hover:bg-[#EEEEEE]"
                  }`}
                >
                  <span>{p.l}</span>
                  {active && <Check size={11} weight="bold" />}
                </button>
              );
            })}
          </div>
          {/* Calendar */}
          <div className="p-3" data-testid="date-range-calendar">
            <Calendar
              mode="range"
              selected={range}
              onSelect={setRange}
              numberOfMonths={2}
              defaultMonth={range?.from || new Date()}
              weekStartsOn={1}
            />
            <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[#EEEEEE]">
              <p className="text-[11px] font-mono text-[#475569]">
                {range?.from ? fmtUK(ymd(range.from)) : "Start"}
                {" → "}
                {range?.to ? fmtUK(ymd(range.to)) : (range?.from ? fmtUK(ymd(range.from)) : "End")}
              </p>
              <Button
                size="sm"
                onClick={applyRange}
                disabled={!range?.from}
                data-testid="date-range-apply"
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md h-8 text-xs"
              >
                Apply range
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
